/**
 * Transfers Service
 *
 * API calls for Transfers.
 * @see Sprint 14 - Transferts Inter-Sites
 * @see US-14.5 - UI Liste et Creation
 */

import { apiClient } from '@/api/client';
import type {
  TransferListItem,
  TransferDetail,
  CreateTransferDto,
  UpdateTransferDto,
  ShipTransferDto,
  ReceiveTransferDto,
  CancelTransferDto,
  QueryTransfersDto,
  ApiSuccess,
  TransfersListResponse,
  TransferDetailResponse,
  CreateTransferResponse,
  UpdateTransferResponse,
  ShipTransferResponse,
  ReceiveTransferResponse,
  CancelTransferResponse,
  TransferKPIsResponse,
  TransferSite,
} from '@bstock/shared';

// Re-export types for convenience
export type {
  TransferListItem,
  TransferDetail,
  CreateTransferDto,
  UpdateTransferDto,
  ShipTransferDto,
  ReceiveTransferDto,
  CancelTransferDto,
  QueryTransfersDto,
  TransferKPIsResponse,
  TransferSite,
};

export const transfersService = {
  /**
   * List transfers with pagination and filters
   */
  async list(params: QueryTransfersDto): Promise<TransfersListResponse> {
    const response = await apiClient.get<ApiSuccess<TransfersListResponse>>(
      '/transfers',
      { params }
    );
    return response.data.data;
  },

  /**
   * Get transfers KPIs
   */
  async getKPIs(): Promise<TransferKPIsResponse> {
    const response = await apiClient.get<ApiSuccess<TransferKPIsResponse>>(
      '/transfers/kpis'
    );
    return response.data.data;
  },

  /**
   * Get available sites for transfer
   */
  async getSites(): Promise<TransferSite[]> {
    const response = await apiClient.get<ApiSuccess<TransferSite[]>>(
      '/transfers/sites'
    );
    return response.data.data;
  },

  /**
   * Get single transfer by ID
   */
  async getById(id: string): Promise<TransferDetailResponse> {
    const response = await apiClient.get<ApiSuccess<TransferDetailResponse>>(
      `/transfers/${id}`
    );
    return response.data.data;
  },

  /**
   * Create a new transfer request
   */
  async create(data: CreateTransferDto): Promise<CreateTransferResponse> {
    const response = await apiClient.post<ApiSuccess<CreateTransferResponse>>(
      '/transfers',
      data
    );
    return response.data.data;
  },

  /**
   * Update a draft transfer
   */
  async update(id: string, data: UpdateTransferDto): Promise<UpdateTransferResponse> {
    const response = await apiClient.patch<ApiSuccess<UpdateTransferResponse>>(
      `/transfers/${id}`,
      data
    );
    return response.data.data;
  },

  /**
   * Delete a draft transfer
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/transfers/${id}`);
  },

  /**
   * Submit a draft transfer (destination site only)
   */
  async submit(id: string): Promise<TransferDetailResponse> {
    const response = await apiClient.post<ApiSuccess<TransferDetailResponse>>(
      `/transfers/${id}/submit`
    );
    return response.data.data;
  },

  /**
   * Ship a transfer (source site only)
   */
  async ship(id: string, data: ShipTransferDto): Promise<ShipTransferResponse> {
    const response = await apiClient.post<ApiSuccess<ShipTransferResponse>>(
      `/transfers/${id}/ship`,
      data
    );
    return response.data.data;
  },

  /**
   * Receive a transfer (destination site only)
   */
  async receive(id: string, data: ReceiveTransferDto): Promise<ReceiveTransferResponse> {
    const response = await apiClient.post<ApiSuccess<ReceiveTransferResponse>>(
      `/transfers/${id}/receive`,
      data
    );
    return response.data.data;
  },

  /**
   * Cancel a transfer
   */
  async cancel(id: string, data: CancelTransferDto): Promise<CancelTransferResponse> {
    const response = await apiClient.post<ApiSuccess<CancelTransferResponse>>(
      `/transfers/${id}/cancel`,
      data
    );
    return response.data.data;
  },
};
